import Ajax from 'devextreme/core/utils/ajax';

/**
 * Blinda o parser de erros OData do DevExtreme contra corpos fora do contrato.
 *
 * ## O bug
 *
 * `formatDotNetError` (`devextreme/esm/__internal/data/odata/m_utils.js`) assume
 * que o envelope de erro é sempre um objeto:
 *
 * ```js
 * const errorObj = response?.then || response?.error
 *               || response?.['odata.error'] || response?.['@odata.error'];
 * if (errorObj) { message = formatDotNetError(errorObj) || message; ... }
 *
 * const formatDotNetError = errorObj => {
 *   let message;
 *   let currentError = errorObj;
 *   if ('message' in errorObj) { ... }                       // (1)
 *   while (currentError = currentError.innererror || currentError.internalexception) {
 *     message = currentError.message ?? message;
 *     if (currentError.internalexception && -1 === message.indexOf('inner exception')) break;  // (2)
 *   }
 *   return message;
 * };
 * ```
 *
 * Quando o backend responde `{"error":"internal_error"}` — string em vez do
 * objeto `{ code, message: { value } }` do OData v4 — a linha (1) estoura com
 * `TypeError: Cannot use 'in' operator to search for 'message' in internal_error`.
 * A linha (2) tem o mesmo problema quando a mensagem acumulada não é string.
 *
 * ## Por que isso não é "só" um erro no Sentry
 *
 * O throw acontece dentro do callback `always` do `sendRequest`, **antes** de
 * qualquer `d.reject`/`d.resolve`:
 *
 * ```js
 * ajax.sendRequest(ajaxOptions).always((obj, textStatus) => {
 *   const tuple = interpretJsonFormat(obj, textStatus, ...);   // <-- lança aqui
 *   ...
 *   if (error) { d.reject(error) } else ... d.resolve(data)    // nunca chega
 * });
 * ```
 *
 * Ou seja, o `Deferred` nunca resolve nem rejeita: a Promise de `ds.load()` fica
 * pendente para sempre e o slot do store que a consome **trava em `loading`** —
 * skeleton infinito, sem mensagem de erro. E o `TypeError` sai pela pilha do
 * `onreadystatechange` do XHR, fora da zona do Angular, chegando ao Sentry como
 * erro global não tratado.
 *
 * ## A correção
 *
 * Um `Ajax.inject` registra um `done`/`fail` no `Deferred` **antes** do
 * `always` do DevExtreme. O `Deferred` dispara os callbacks na ordem de
 * registro, então o corpo já chega normalizado a `interpretJsonFormat`:
 * envelope primitivo vira `{ message: '<primitivo>' }` e toda `message` que o
 * DevExtreme leria como não-string vira `''` (falsy, então ele cai no fallback
 * `errorMessageFromXhr`). Com isso o `d.reject(error)` volta a acontecer e o
 * `tapResponse`/`setPropError` do store recebe o erro normalmente.
 *
 * O hook é global (o `Ajax` do DevExtreme é um `dependency_injector`), por isso
 * só age em requests da camada OData do próprio DevExtreme — identificados pelo
 * `accepts.json` que ela carimba em `ajaxOptionsForRequest`.
 */

/** `accepts.json` que a camada OData do DevExtreme carimba em todo request seu. */
const ODATA_ACCEPTS_JSON = 'application/json;odata=verbose,text/plain';

/** Chaves que o DevExtreme testa, em ordem, para achar o envelope de erro. */
const ERROR_ENVELOPE_KEYS = ['then', 'error', 'odata.error', '@odata.error'];

/** Corta cadeias `innererror`/`internalexception` cíclicas ou absurdamente fundas. */
const MAX_INNER_ERROR_DEPTH = 20;

let installed = false;

/**
 * Instala o sanitizador uma única vez por aplicação. Idempotente — chamada no
 * construtor do `DxOdataStoreService`, que é `providedIn: 'root'` e é o único
 * ponto do monorepo que constrói um `ODataStore`.
 */
export function installDxODataErrorBodySanitizer(): void {
  if (installed) return;
  installed = true;

  Ajax.inject({
    sendRequest(options: { accepts?: { json?: string } }) {
      const promise = this.callBase(options);

      if (options?.accepts?.json !== ODATA_ACCEPTS_JSON) return promise;

      return promise
        // Caminho 2xx: o DevExtreme resolve com o JSON já parseado e o entrega
        // direto a `interpretJsonFormat` — dá para normalizar in place.
        .done((body: unknown) => {
          try {
            sanitizeODataErrorEnvelope(body);
          } catch {
            /* nunca deixar o sanitizador derrubar o request que ele protege */
          }
        })
        // Caminho de erro: o DevExtreme rejeita com o próprio XHR e reparseia
        // `responseText`, então é o texto que precisa ser reescrito.
        .fail((xhr: XMLHttpRequest) => {
          try {
            sanitizeXhrResponseText(xhr);
          } catch {
            /* idem */
          }
        });
    },
  });
}

/**
 * Solta o guard de idempotência. Existe para os testes, que precisam reinstalar
 * o hook depois de um `Ajax.resetInjection()` — a injeção do DevExtreme é uma
 * cadeia de herança, e o transporte falso do teste tem de entrar **antes** do
 * sanitizador para que o `callBase` dele o alcance.
 */
export function resetDxODataErrorBodySanitizer(): void {
  installed = false;
}

/**
 * Reescreve `xhr.responseText` com o corpo normalizado, quando há o que
 * normalizar. `responseText` é um accessor do protótipo do `XMLHttpRequest`;
 * uma propriedade própria o sombreia sem tocar na instância real da resposta.
 */
function sanitizeXhrResponseText(xhr: XMLHttpRequest): void {
  const raw = xhr?.responseText;
  if (typeof raw !== 'string' || raw === '') return;

  let body: unknown;
  try {
    body = JSON.parse(raw);
  } catch {
    // Corpo não-JSON: o `JSON.parse` do próprio DevExtreme também falha e ele
    // segue com a mensagem do `errorMessageFromXhr`. Nada a fazer.
    return;
  }

  if (!sanitizeODataErrorEnvelope(body)) return;

  Object.defineProperty(xhr, 'responseText', {
    value: JSON.stringify(body),
    configurable: true,
    enumerable: false,
    writable: false,
  });
}

/**
 * Normaliza, in place, o envelope de erro de um corpo OData para a forma que
 * `formatDotNetError` consegue ler. Retorna `true` se alterou algo.
 *
 * Exportada para teste — em produção só é chamada pelo hook do `Ajax.inject`.
 */
export function sanitizeODataErrorEnvelope(body: unknown): boolean {
  if (!isRecord(body)) return false;

  let changed = false;

  for (const key of ERROR_ENVELOPE_KEYS) {
    if (!(key in body)) continue;

    const node = body[key];
    // Falsy é ignorado pelo DevExtreme (`if (errorObj)`), e objetos/funções
    // sobrevivem ao `in` sem estourar.
    if (!node) continue;

    if (typeof node !== 'object' && typeof node !== 'function') {
      body[key] = { message: stringify(node) };
      changed = true;
      continue;
    }

    if (isRecord(node)) changed = sanitizeErrorChain(node) || changed;
  }

  return changed;
}

/**
 * Percorre a cadeia `innererror`/`internalexception` exatamente como
 * `formatDotNetError` percorre, garantindo que a `message` que ele acumula seja
 * sempre uma string na hora do `message.indexOf('inner exception')`.
 *
 * Só o nó raiz tem a forma `{ value }` desembrulhada pelo DevExtreme
 * (`errorObj.message?.value || errorObj.message`); nos nós internos ele lê
 * `currentError.message` cru, então lá um `{ value }` também é inválido.
 */
function sanitizeErrorChain(root: Record<string, unknown>): boolean {
  let changed = false;
  let message: unknown;

  if ('message' in root) {
    const raw = root['message'];
    message = isRecord(raw) && typeof raw['value'] === 'string' ? raw['value'] : raw;

    if (typeof message !== 'string') {
      // `''` é falsy: `formatDotNetError` retorna vazio e o DevExtreme cai no
      // fallback `errorMessageFromXhr` (statusText), que é mais informativo do
      // que um placeholder nosso.
      root['message'] = '';
      message = '';
      changed = true;
    }
  }

  let current = root;

  for (let depth = 0; depth < MAX_INNER_ERROR_DEPTH; depth++) {
    const next = current['innererror'] || current['internalexception'];
    // Sem próximo nó, ou próximo nó não-objeto: o DevExtreme lê `.message` e
    // `.innererror` como `undefined` e encerra o laço sem estourar.
    if (!next || !isRecord(next)) break;

    const raw = next['message'];
    if (raw !== undefined && raw !== null) {
      if (typeof raw === 'string') {
        message = raw;
      } else {
        next['message'] = '';
        message = '';
        changed = true;
      }
    }

    // O `indexOf` só roda quando há `internalexception`; aí a mensagem
    // acumulada (que pode nem existir) precisa ser uma string.
    if (next['internalexception'] && typeof message !== 'string') {
      next['message'] = '';
      message = '';
      changed = true;
    }

    current = next;
  }

  return changed;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** `String(symbol)` estoura; tudo o mais aqui é primitivo seguro de coagir. */
function stringify(value: unknown): string {
  return typeof value === 'symbol' ? value.toString() : String(value);
}
